// Settings, AMS360, AMS360 web address (spec C13; the Manager and the Admin): the agency's AMS360 address
// with its version ("https://www.ams360.com/v2623881"), which every Open in AMS360 link starts with.
// AMS360 needs the version and may change it with an update; then the links open AMS360's error page
// until the Manager or Admin saves the new one here. Test link opens a customer in AMS360 in a new tab.
// Markup from the Settings board's Integrations cards (icard, ih, esub) with its own Save and History.
import { useState } from 'react';
import type { AmsAddressView, Api } from '../auth/api';
import { useOnline } from '../shell/online';
import { Toast } from '../shell/Toast';
import { ExternalLinkIcon } from '../shell/icons';
import { HistoryRows } from './HistoryRows';
import { amsAddressCopy as c, amsHistoryRow, parseAmsAddress } from './ams-address-lines';

export function AmsAddressCard({ api, initial }: { api: Api; initial: AmsAddressView }) {
  const [view, setView] = useState(initial);
  const [draft, setDraft] = useState(initial.address);
  const [histOpen, setHistOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  const online = useOnline();

  const parsed = parseAmsAddress(draft);
  const dirty = draft.trim() !== view.address;
  const invalid = dirty && draft.trim() !== '' && !parsed;
  const canSave = dirty && !!parsed && !busy && online;

  async function save() {
    if (!canSave || !parsed) return;
    setBusy(true);
    setError(null);
    const res = await api.saveAmsAddress(parsed);
    setBusy(false);
    if (!res.ok) {
      setError(res.error === 'bad_address' ? c.invalid : c.somethingWrong);
      return;
    }
    setView(res.body);
    setDraft(res.body.address);
    setToast({ id: Date.now(), text: c.saved });
  }

  return (
    <div className="col g8">
      <div className="icard">
        <div className="ih">
          <span className="dot ok" />
          <span className="t14 w6 ink">{c.title}</span>
          <span className="t12 muted">{c.sub}</span>
        </div>
        <div className="col g4">
          <label className="flab m0" htmlFor="ams-address">
            {c.label}
          </label>
          <div className="row g8 wrap">
            <input
              id="ams-address"
              className="input mono grow"
              value={draft}
              spellCheck={false}
              aria-invalid={invalid ? 'true' : 'false'}
              aria-describedby={invalid ? 'ams-address-err' : undefined}
              onChange={(e) => setDraft(e.target.value)}
            />
            {view.testLink && (
              <a className="btn" href={view.testLink} target="_blank" rel="noopener noreferrer">
                <ExternalLinkIcon className="ico s14" />
                {c.test}
              </a>
            )}
          </div>
          {invalid && (
            <p id="ams-address-err" className="ferr">
              {c.invalid}
            </p>
          )}
        </div>
        <p className="esub m0">
          {c.note} {c.defaultLine(view.defaultAddress)}
        </p>
        <div className="row g8 wrap">
          <button
            type="button"
            className="linkbtn"
            aria-expanded={histOpen}
            onClick={() => setHistOpen(!histOpen)}
          >
            {histOpen ? c.hideHistory : c.history(view.history.length)}
          </button>
          <span className="grow" />
          {dirty && <span className="t12 amber">{c.unsaved}</span>}
          <button
            type="button"
            className="btn"
            disabled={!dirty}
            onClick={() => {
              setDraft(view.address);
              setError(null);
            }}
          >
            {c.cancel}
          </button>
          <button
            type="button"
            className={canSave ? 'btn btn-primary' : 'btn'}
            disabled={!canSave}
            onClick={() => void save()}
          >
            {c.save}
          </button>
        </div>
      </div>
      {histOpen && <HistoryRows rows={view.history.map(amsHistoryRow)} empty={c.noHistory} />}
      {error && (
        <p className="ferr" role="alert">
          {error}
        </p>
      )}
      {toast && <Toast key={toast.id} text={toast.text} />}
    </div>
  );
}
